<template>
  <div>
    <Modal
      :width="500"
      :value="modalOpen"
      :title="modalData.title"
      :subtitle="modalData.subtitle"
      :cta-text="modalData.ctaText"
      :cancel-hidden="modalData.cancelHidden"
      @cancel="modalOpen = false"
      dialogIcon="danger"
      @confirm="confirmModal">
    </Modal>
    <div ref="canvas" class="lc-graph" v-if="graph.nodes.length > 0" :style="canvasStyle">
      <svg class="lc-edges" :width="canvasSize.width" :height="canvasSize.height" xmlns="http://www.w3.org/2000/svg">
        <g v-for="edge in layoutEdges" :key="edge.id" class="lc-edge-group">
          <path
            v-if="edge.type === 'next'"
            class="lc-edge-hit"
            :d="edge.d"
            vector-effect="non-scaling-stroke"
            @click.stop="onEdgeClick(edge)"
            @mouseenter="hoveredEdgeId = edge.id"
            @mouseleave="hoveredEdgeId = null"></path>
          <path
            class="lc-edge"
            :class="{ 'lc-edge--next': edge.type === 'next' }"
            :d="edge.d"
            vector-effect="non-scaling-stroke"></path>
        </g>
        <!-- svg has no z-index; these repaints come last so highlights sit above every other line -->
        <path
          v-for="edge in linkedEdges"
          :key="`linked-${edge.id}`"
          class="lc-edge lc-edge--linked"
          :d="edge.d"
          vector-effect="non-scaling-stroke"></path>
        <path
          v-if="hoveredEdge"
          class="lc-edge lc-edge--next lc-edge--hover"
          :d="hoveredEdge.d"
          vector-effect="non-scaling-stroke"></path>
      </svg>
      <div
        v-for="entry in graph.nodes"
        :key="entry.id"
        class="lc-gnode"
        :data-graph-id="entry.id"
        :style="gnodeStyle(entry.id)"
        @mouseenter="hoveredNodeId = entry.id"
        @mouseleave="hoveredNodeId = null">
        <LiveChatTreeNode
          :flat="true"
          :node="entry.node"
          :parent="entry.parent"
          :index="entry.index"
          :lastMessageMap="lastMessageMap"
          @openModal="openModal"
          @recomputeLastMessageMap="computeLastMessageMap"
          @pruneDanglingNext="onPruneDanglingNext"></LiveChatTreeNode>
      </div>
    </div>
    <div v-else-if="Array.isArray(messageTree) && messageTree.length" class="lc-graph-empty">
      These messages can't be drawn — the flow data is not in the expected shape. Switch to JSON mode to inspect it.
    </div>
  </div>
</template>

<script>
  import LiveChatTreeNode from "./LiveChatTreeNode.vue";
  import Modal from "@/components/custom/Modal.vue";
  import cloneDeep from "lodash/cloneDeep";
  import {
    flattenMessageTree,
    findNodeById,
    collectNodeIds,
    buildEdges,
    idsThatReach,
    normalizeIds,
  } from "./treeGraph";
  import { computeTreeLayout } from "./treeLayout";

  export default {
    components: { LiveChatTreeNode, Modal },
    props: {
      messageTree: {
        type: Array,
        required: true,
      },
    },
    provide() {
      return {
        linkContext: {
          state: this.linking,
          startLink: this.startLink,
          completeLink: this.completeLink,
          cancelLink: this.cancelLink,
          removeLink: this.removeLink,
          hoverLink: this.hoverLink,
          isValidTarget: this.isValidTarget,
        },
      };
    },
    data() {
      return {
        modalOpen: false,
        modalData: {},
        styleElement: null,
        lastMessageMap: {},
        linking: { sourceId: null, forbidden: null },
        hoveredEdgeId: null,
        hoveredNodeId: null,
        hoveredLinkSourceId: null,
        layoutNodes: {}, // id -> { x, y, w, h } (top-left corner, local coords)
        layoutEdges: [],
        canvasSize: { width: 0, height: 0 },
        resizeObserver: null,
        nodeSizes: {},
        layingOut: false,
        pendingForce: false,
        layoutTimer: null,
      };
    },
    computed: {
      tinymceCss() {
        return this.$store.state.tinymceCss;
      },
      graph() {
        return flattenMessageTree(this.messageTree);
      },
      // Identity of the current node set — re-observe/relayout when nodes are added/removed.
      nodeIds() {
        return this.graph.nodes.map(n => n.id).join(",");
      },
      // Highlighted from either end: the edge itself, or the port that removes it.
      hoveredEdge() {
        return (
          this.layoutEdges.find(
            edge =>
              edge.id === this.hoveredEdgeId ||
              (edge.type === "next" && edge.from === this.hoveredLinkSourceId),
          ) || null
        );
      },
      // Every edge touching the hovered node, either direction, so its connections are traceable at a glance.
      linkedEdges() {
        if (!this.hoveredNodeId) return [];
        return this.layoutEdges.filter(
          edge => edge.from === this.hoveredNodeId || edge.to === this.hoveredNodeId,
        );
      },
      canvasStyle() {
        return {
          position: "relative",
          width: `${this.canvasSize.width}px`,
          height: `${this.canvasSize.height}px`,
          margin: "0 auto",
        };
      },
    },
    watch: {
      tinymceCss: {
        immediate: true,
        handler(newCss) {
          this.updateStyles(newCss);
        },
      },
      messageTree: {
        deep: true,
        immediate: true,
        handler() {
          // Id-less nodes render a blank canvas; idempotent, so the re-triggered pass writes nothing.
          normalizeIds(this.messageTree);
          // Only the cheap ending map here — relayout is driven by `nodeIds`/ResizeObserver (gated), not keystrokes.
          this.computeLastMessageMap();
        },
      },
      nodeIds() {
        this.$nextTick(() => {
          this.setupObservers();
          this.scheduleLayout(true);
        });
      },
    },
    mounted() {
      this.setupObservers();
      this.scheduleLayout(true);
      window.addEventListener("resize", this.onWindowResize);
    },
    beforeDestroy() {
      if (this.styleElement) {
        this.styleElement.remove();
        this.styleElement = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      clearTimeout(this.layoutTimer);
      window.removeEventListener("resize", this.onWindowResize);
      document.removeEventListener("keydown", this.onLinkKeydown);
    },
    methods: {
      updateStyles(css) {
        if (!this.styleElement) {
          this.styleElement = document.createElement("style");
          document.head.appendChild(this.styleElement);
        }

        this.styleElement.innerHTML = this.scopeCss(css, ".content-preview");
      },
      scopeCss(css, scopeSelector) {
        return css.replace(/(^|\})\s*([^\{]+)/g, (match, closingBracket, selectors) => {
          if (closingBracket) {
            return `${closingBracket} ${selectors
              .split(",")
              .map(sel => `${scopeSelector} ${sel.trim()}`)
              .join(",")}`;
          }

          return `${scopeSelector} ${selectors}`;
        });
      },
      openModal(data) {
        this.modalOpen = true;
        this.modalData = data;
      },
      confirmModal() {
        this.modalOpen = false;
        if (this.modalData.removeLinkSourceId) {
          this.doRemoveLink(this.modalData.removeLinkSourceId);
          return;
        }
        if (this.modalData.delete) {
          const children = cloneDeep(this.modalData.parent.children);
          this.modalData.parent.children = [];

          this.$nextTick(() => {
            children.splice(this.modalData.index, 1);
            this.modalData.parent.children = children;
            this.onPruneDanglingNext();
          });
        }
      },
      onPruneDanglingNext() {
        const ids = collectNodeIds(this.messageTree);

        const prune = node => {
          if (!node || typeof node !== "object") return;
          if (node.next && !ids.has(node.next)) {
            this.$set(node, "next", null);
          }
          if (Array.isArray(node.children)) node.children.forEach(prune);
        };
        this.messageTree.forEach(prune);

        // A cleared `next` may turn a source back into an ending — refresh the map.
        this.computeLastMessageMap();
        this.scheduleLayout(true);
      },
      startLink(sourceId) {
        // Forbidden targets (self + predecessors) precomputed once → O(1) isValidTarget per port.
        this.linking.forbidden = idsThatReach(this.messageTree, sourceId);
        this.linking.sourceId = sourceId;
        document.addEventListener("keydown", this.onLinkKeydown);
      },
      cancelLink() {
        this.linking.sourceId = null;
        this.linking.forbidden = null;
        document.removeEventListener("keydown", this.onLinkKeydown);
      },
      onLinkKeydown(event) {
        if (event.key === "Escape") this.cancelLink();
      },
      completeLink(targetId) {
        const sourceId = this.linking.sourceId;
        if (!sourceId) return;
        // Re-check against the live tree — the cached per-port `forbidden` set goes stale if structure changed.
        if (idsThatReach(this.messageTree, sourceId).has(targetId)) {
          if (this.$admin && this.$admin.toast) {
            this.$admin.toast.error("Cannot link here — it would create a loop.");
          }
          return;
        }
        const source = findNodeById(this.messageTree, sourceId);
        // Setting `next` is only valid when the source owns no children (mutual exclusivity).
        if (!source || (source.children && source.children.length)) {
          this.cancelLink();
          return;
        }
        this.$set(source, "next", targetId);
        this.cancelLink();
        this.computeLastMessageMap();
        // Relayout first, so the connector is never drawn routing up-and-over a target that hasn't moved down yet.
        this.scheduleLayout(true);
      },
      removeLink(sourceId) {
        this.openModal({
          title: "Remove branch?",
          subtitle: "This will disconnect this branch from the message it continues to. You can link it again later.",
          ctaText: "Remove",
          removeLinkSourceId: sourceId,
        });
      },
      doRemoveLink(sourceId) {
        const source = findNodeById(this.messageTree, sourceId);
        if (source) this.$set(source, "next", null);
        this.cancelLink();
        this.computeLastMessageMap();
        this.$nextTick(() => {
          this.layoutEdges = this.edgesFromLayoutNodes();
        });
        this.scheduleLayout(true);
      },
      hoverLink(sourceId) {
        this.hoveredLinkSourceId = sourceId;
      },
      onEdgeClick(edge) {
        if (edge.type === "next") this.removeLink(edge.from);
      },
      isValidTarget(targetId) {
        const { sourceId, forbidden } = this.linking;
        if (!sourceId || !forbidden) return false;
        return !forbidden.has(targetId);
      },
      setupObservers() {
        if (typeof ResizeObserver === "undefined") return;
        const canvas = this.$refs.canvas;
        if (!canvas) return;
        if (!this.resizeObserver) {
          // Not forced — passes through the >2px gate so observe -> layout -> observe can't thrash.
          this.resizeObserver = new ResizeObserver(() => this.scheduleLayout(false));
        }
        this.resizeObserver.disconnect();
        canvas.querySelectorAll(".lc-gnode").forEach(el => this.resizeObserver.observe(el));
      },
      scheduleLayout(force = false) {
        if (force) this.pendingForce = true;
        clearTimeout(this.layoutTimer);
        const delay = force ? 50 : 220;
        this.layoutTimer = setTimeout(() => {
          const runForce = this.pendingForce;
          this.pendingForce = false;
          this.measureAndLayout(runForce);
        }, delay);
      },
      onWindowResize() {
        this.scheduleLayout(false);
      },
      measureAndLayout(force = false) {
        if (this.layingOut) return;
        this.$nextTick(() => {
          const canvas = this.$refs.canvas;
          if (!canvas) {
            this.layoutNodes = {};
            this.layoutEdges = [];
            return;
          }
          this.layingOut = true;
          try {
            const graph = this.graph;
            if (!graph.nodes.length) {
              this.nodeSizes = {};
              this.layoutNodes = {};
              this.layoutEdges = [];
              this.canvasSize = { width: 0, height: 0 };
              return;
            }

            const sizes = {};
            let sizeChanged = false;
            canvas.querySelectorAll(".lc-gnode").forEach(el => {
              const id = el.getAttribute("data-graph-id");
              if (!id) return;
              const width = el.offsetWidth;
              const height = el.offsetHeight;
              sizes[id] = { width, height };
              const prev = this.nodeSizes[id];
              if (!prev || Math.abs(prev.width - width) > 2 || Math.abs(prev.height - height) > 2) {
                sizeChanged = true;
              }
            });

            // No structural force and no size change — skip (stops observer loop + per-keystroke relayout).
            if (!force && !sizeChanged) return;

            this.nodeSizes = sizes;
            const { layoutNodes, layoutEdges, canvasSize } = computeTreeLayout(graph, sizes, this.messageTree);
            this.layoutNodes = layoutNodes;
            this.layoutEdges = layoutEdges;
            this.canvasSize = canvasSize;
          } catch (error) {
            // Never break the editor if layout throws — keep the prior positions.
            // eslint-disable-next-line no-console
            console.warn("[LiveChat] graph layout failed:", error);
          } finally {
            this.layingOut = false;
          }
        });
      },
      // Instant edge rebuild from current positions (no dagre) for immediate link/unlink feedback.
      edgesFromLayoutNodes() {
        return buildEdges(this.graph.edges, id => {
          const pos = this.layoutNodes[id];
          return pos && { cx: pos.x + pos.w / 2, top: pos.y, bottom: pos.y + pos.h };
        });
      },
      gnodeStyle(id) {
        const pos = this.layoutNodes[id];
        return {
          position: "absolute",
          left: `${pos ? pos.x : 0}px`,
          top: `${pos ? pos.y : 0}px`,
          // Hidden until positioned, so the first pass doesn't flash boxes at the origin.
          visibility: pos ? "visible" : "hidden",
        };
      },
      computeLastMessageMap() {
        const map = {};
        const roots = Array.isArray(this.messageTree) ? this.messageTree : [];

        const traverse = (node, lastMsgId) => {
          let currentLastMsgId = lastMsgId;
          if (node && node.messageType === "message" && node.id) {
            currentLastMsgId = node.id;
          }

          const hasChildren = Array.isArray(node?.children) && node.children.length > 0;
          if (hasChildren) {
            node.children.forEach(child => traverse(child, currentLastMsgId));
          } else if (node?.next) {
            // Branch continues at another node via `next` — not an ending, don't mark.
          } else if (currentLastMsgId) {
            map[currentLastMsgId] = true;
          }
        };

        roots.forEach(root => traverse(root, null));
        this.lastMessageMap = map;
      },
    },
  };
</script>

<style lang="scss">
  @import "@/@core/preset/preset/shortcodes.scss";
</style>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";

  .lc-graph {
    position: relative;
  }

  .lc-graph-empty {
    padding: 16px;
    text-align: center;
    color: get-color("error");
  }

  .lc-edges {
    position: absolute;
    top: 0;
    left: 0;
    overflow: visible;
    // Nodes sit above the edge layer; only `next` edges opt back into pointer events.
    pointer-events: none;
    z-index: 0;
  }

  .lc-edge {
    fill: none;
    stroke: get-color("subtitle");
    stroke-width: 2.5;
    vector-effect: non-scaling-stroke;
    pointer-events: none;
    transition: none;

    &--next {
      stroke: get-color("primary");
    }
  }

  // Wide transparent hit target so the thin `next` line is easy to click for removal.
  .lc-edge-hit {
    fill: none;
    stroke: transparent;
    stroke-width: 22;
    vector-effect: non-scaling-stroke;
    pointer-events: stroke;
    cursor: pointer;
  }

  // Connections of the hovered node. Green, not info blue: blue sits too close to the primary
  // purple of a normal `next` edge, and red already means "click to remove".
  .lc-edge--linked {
    stroke: get-color("success");
    stroke-width: 4;
  }

  .lc-edge--hover {
    stroke: get-color("error");
    stroke-width: 4;
  }

  .lc-gnode {
    z-index: 1;
    // Never animate position — otherwise nodes glide on every relayout.
    transition: none !important;
  }
</style>
